import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, MapPin, Sparkles, Users, Video } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Alert } from '@/client/components/ui/alert';
import { Seo } from '@/client/components/Seo';
import { api, ApiError } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { isLocale, type Locale } from '@/client/i18n';
import { useMe } from '@/client/hooks/useMe';
import type {
  ConsultationTypePublic,
  AvailableSlot,
} from '@/client/types/public-consultations';
import type { QuestionnaireField } from '@/client/types/consultations';
import { fromUnixSeconds, formatHHMM } from '@/client/lib/datetime';

const FORMAT_LABEL_SK: Record<ConsultationTypePublic['format'], string> = {
  online: 'Online',
  in_person: 'Osobne',
  hybrid: 'Online aj osobne',
};
const FORMAT_LABEL_CS: Record<ConsultationTypePublic['format'], string> = {
  online: 'Online',
  in_person: 'Osobně',
  hybrid: 'Online i osobně',
};
const FORMAT_ICON: Record<ConsultationTypePublic['format'], typeof Video> = {
  online: Video,
  in_person: MapPin,
  hybrid: Users,
};

export function ConsultationDetail() {
  const { slug, locale: localeParam } = useParams<{ slug: string; locale: string }>();
  const locale: Locale = isLocale(localeParam) ? localeParam : 'sk';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();

  const detail = useQuery({
    queryKey: ['public', 'consultations', slug],
    queryFn: () => api<ConsultationTypePublic>(`/api/v1/consultations/${slug}`),
    enabled: !!slug,
  });

  const fromTs = Math.floor(Date.now() / 1000);
  const toTs = fromTs + 90 * 24 * 3600;
  const slots = useQuery({
    queryKey: ['public', 'consultations', slug, 'slots', fromTs],
    queryFn: () =>
      api<AvailableSlot[]>(`/api/v1/consultations/${slug}/slots?from=${fromTs}&to=${toTs}`),
    enabled: !!slug,
  });

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Record<string, unknown>>({});
  const [contact, setContact] = useState<{ name: string; email: string; phone: string }>(() => ({
    name: '',
    email: '',
    phone: '',
  }));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me) {
      setContact((c) => ({
        name: c.name || me.name,
        email: c.email || me.email,
        phone: c.phone,
      }));
    }
  }, [me]);

  const submit = useMutation({
    mutationFn: () => {
      if (!selectedSlotId) throw new Error('No slot');
      return api<{ bookingId: string }>(`/api/v1/consultations/${slug}/bookings`, {
        method: 'POST',
        body: {
          slotId: selectedSlotId,
          questionnaireResponse: questionnaire,
          contact,
        },
      });
    },
    onSuccess: (resp) => {
      navigate(`/${locale}/konzultacie/odoslane?bookingId=${resp.bookingId}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          locale === 'cs'
            ? 'Termín už není dostupný. Vyber jiný.'
            : 'Termín už nie je dostupný. Vyber iný.',
        );
        slots.refetch();
        setSelectedSlotId(null);
      } else if (err instanceof ApiError && err.status === 422) {
        setError(
          locale === 'cs'
            ? 'Vyplň prosím všechna povinná pole.'
            : 'Vyplň prosím všetky povinné polia.',
        );
      } else {
        setError(
          locale === 'cs' ? 'Něco se pokazilo. Zkus znovu.' : 'Niečo sa pokazilo. Skús znova.',
        );
      }
    },
  });

  if (detail.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Alert variant="error">
          {locale === 'cs' ? 'Konzultace nenalezena.' : 'Konzultácia sa nenašla.'}
        </Alert>
      </div>
    );
  }

  const type = detail.data;
  const formatter = new Intl.NumberFormat(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
    style: 'currency',
    currency: type.currency,
    minimumFractionDigits: 0,
  });
  const FormatIcon = FORMAT_ICON[type.format];
  const formatLabel = locale === 'cs' ? FORMAT_LABEL_CS[type.format] : FORMAT_LABEL_SK[type.format];
  const canSubmit =
    selectedSlotId &&
    contact.name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(contact.email) &&
    !submit.isPending;

  return (
    <div>
      <Seo
        title={`${type.title} — ${t('seo.consultations_title')}`}
        description={type.subtitle ?? t('seo.consultations_description')}
        locale={locale}
      />

      {/* Hero */}
      <section className="relative">
        <div
          className="absolute top-10 -left-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-12">
          <Link
            to={`/${locale}/konzultacie`}
            className="inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} /> {locale === 'cs' ? 'Konzultace' : 'Konzultácie'}
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2 animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary/8 border border-primary/15 text-primary px-2.5 py-1 font-sans text-caption font-medium">
              <FormatIcon size={11} />
              {formatLabel}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-white/70 border border-glass-border-strong text-text-secondary px-2.5 py-1 font-sans text-caption font-medium">
              {type.durationMinutes} min
            </span>
            {type.withDavid && (
              <span className="inline-flex items-center gap-1.5 rounded-pill bg-white/70 border border-glass-border-strong text-text-secondary px-2.5 py-1 font-sans text-caption font-medium">
                {locale === 'cs' ? 's Davidem' : 's Davidom'}
              </span>
            )}
          </div>

          <h1
            className="mt-4 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
            style={{ animationDelay: '70ms' }}
          >
            {type.title}
          </h1>
          {type.subtitle && (
            <p
              className="mt-3 font-display text-body-large text-text-secondary leading-relaxed animate-fade-up"
              style={{ animationDelay: '140ms' }}
            >
              {type.subtitle}
            </p>
          )}

          <div
            className="mt-6 inline-flex items-baseline gap-2 animate-fade-up"
            style={{ animationDelay: '210ms' }}
          >
            <p className="font-display text-[2rem] leading-none text-primary-dark">
              {formatter.format(type.priceCents / 100)}
            </p>
            <span className="font-sans text-body-small text-text-muted">
              {locale === 'cs' ? '/ konzultace' : '/ konzultácia'}
            </span>
          </div>
        </div>
      </section>

      {/* About + how it works */}
      <section className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 pb-10">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="dr-card-static p-7">
            <p className="dr-eyebrow">
              {locale === 'cs' ? 'O konzultaci' : 'O konzultácii'}
            </p>
            <article className="mt-3 prose-sm font-sans text-body text-text-primary whitespace-pre-line leading-relaxed">
              {type.descriptionMd}
            </article>
          </div>
          <div className="dr-card-static p-7">
            <p className="dr-eyebrow">
              {locale === 'cs' ? 'Jak to funguje' : 'Ako to funguje'}
            </p>
            <ol className="mt-4 space-y-3">
              {(locale === 'cs'
                ? ['Vyber termín', 'Vyplň krátký dotazník', 'Počkej na schválení do 48 h', 'Zaplať a setkáš se s Dajanou']
                : ['Vyber termín', 'Vyplň krátky dotazník', 'Počkaj na schválenie do 48 h', 'Zaplať a stretni sa s Dajanou']
              ).map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-pill bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 font-display text-body-small text-primary">
                    {i + 1}
                  </span>
                  <p className="font-sans text-body text-text-primary leading-relaxed pt-0.5">
                    {step}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Slot picker */}
      <section className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 py-10">
        <div className="dr-card-static p-7 sm:p-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="dr-eyebrow">
                {locale === 'cs' ? 'Termíny' : 'Termíny'}
              </p>
              <h2 className="mt-2 font-display text-h3 text-primary-dark">
                {locale === 'cs' ? 'Vyber termín' : 'Vyber termín'}
              </h2>
            </div>
          </div>
          <div className="mt-6">
            <SlotPicker
              slots={slots.data ?? []}
              isLoading={slots.isLoading}
              locale={locale}
              selectedSlotId={selectedSlotId}
              onSelect={setSelectedSlotId}
            />
          </div>
        </div>
      </section>

      {selectedSlotId && (
        <section className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 pb-16">
          {type.preQuestionnaireSchema && type.preQuestionnaireSchema.fields.length > 0 && (
            <div className="dr-card-static p-7 sm:p-8 mb-6 animate-fade-up">
              <p className="dr-eyebrow">
                {locale === 'cs' ? 'Krátký dotazník' : 'Krátky dotazník'}
              </p>
              <h2 className="mt-2 font-display text-h3 text-primary-dark">
                {locale === 'cs' ? 'Pomôž nám pripraviť sa' : 'Pomôž nám pripraviť sa'}
              </h2>
              <div className="mt-6 space-y-4">
                {type.preQuestionnaireSchema.fields.map((field) => (
                  <QuestionnaireFieldInput
                    key={field.key}
                    field={field}
                    locale={locale}
                    value={questionnaire[field.key]}
                    onChange={(v) => setQuestionnaire((q) => ({ ...q, [field.key]: v }))}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="dr-card-static p-7 sm:p-8 animate-fade-up">
            <p className="dr-eyebrow">
              {locale === 'cs' ? 'Kontaktní údaje' : 'Kontaktné údaje'}
            </p>
            <h2 className="mt-2 font-display text-h3 text-primary-dark">
              {locale === 'cs' ? 'Tvé kontaktní údaje' : 'Tvoje kontaktné údaje'}
            </h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <Label>{locale === 'cs' ? 'Jméno' : 'Meno'}</Label>
                <Input
                  value={contact.name}
                  onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <Label>{locale === 'cs' ? 'Telefon (volitelné)' : 'Telefón (voliteľné)'}</Label>
                <Input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                />
              </div>
            </div>

            {error && (
              <Alert variant="error" className="mt-6">
                {error}
              </Alert>
            )}

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <Button
                size="lg"
                onClick={() => {
                  setError(null);
                  submit.mutate();
                }}
                disabled={!canSubmit}
                className="w-full sm:w-auto"
              >
                {submit.isPending
                  ? locale === 'cs'
                    ? 'Odesílám…'
                    : 'Odosielam…'
                  : locale === 'cs'
                    ? 'Pošli žádost'
                    : 'Pošli žiadosť'}
                <ArrowRight size={16} />
              </Button>
              <p className="font-sans text-caption text-text-muted leading-relaxed flex items-start gap-1.5">
                <Sparkles size={11} className="text-primary mt-0.5 shrink-0" />
                {locale === 'cs'
                  ? 'Termín ti rezervujeme na 48 hodin, dokud Dajana nepotvrdí.'
                  : 'Termín ti rezervujeme na 48 hodín, kým Dajana nepotvrdí.'}
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function SlotPicker({
  slots,
  isLoading,
  locale,
  selectedSlotId,
  onSelect,
}: {
  slots: AvailableSlot[];
  isLoading: boolean;
  locale: Locale;
  selectedSlotId: string | null;
  onSelect: (id: string) => void;
}) {
  const grouped = useMemo(() => {
    const byDay = new Map<string, AvailableSlot[]>();
    for (const s of slots) {
      const d = fromUnixSeconds(s.startAt);
      const key = d.toDateString();
      const arr = byDay.get(key) ?? [];
      arr.push(s);
      byDay.set(key, arr);
    }
    return Array.from(byDay.entries()).map(([key, list]) => ({
      key,
      date: new Date(key),
      slots: list.sort((a, b) => a.startAt - b.startAt),
    }));
  }, [slots]);

  if (isLoading)
    return (
      <p className="font-sans text-body text-text-secondary inline-flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
        …
      </p>
    );
  if (slots.length === 0) {
    return (
      <div className="rounded-2xl bg-white/40 border border-glass-border p-6 text-center">
        <p className="font-sans text-body text-text-secondary leading-relaxed">
          {locale === 'cs'
            ? 'Aktuálně žádný volný termín. Sleduj nás — Dajana otevře další termíny brzy.'
            : 'Aktuálne žiadny voľný termín. Sleduj nás — Dajana otvorí ďalšie termíny čoskoro.'}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {grouped.map(({ key, date, slots }) => (
        <div key={key}>
          <p className="font-sans text-caption uppercase tracking-[0.14em] text-primary mb-2.5">
            {date.toLocaleDateString(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => {
              const isSelected = selectedSlotId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSelect(s.id)}
                  className={cn(
                    'rounded-pill px-4 h-10 font-sans text-body-small font-medium transition-all',
                    isSelected
                      ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_4px_14px_rgba(117,59,189,0.35)]'
                      : 'bg-white/70 backdrop-blur-md border border-glass-border-strong text-text-primary hover:border-primary/40 hover:text-primary hover:-translate-y-px',
                  )}
                >
                  {formatHHMM(fromUnixSeconds(s.startAt))}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function QuestionnaireFieldInput({
  field,
  locale,
  value,
  onChange,
}: {
  field: QuestionnaireField;
  locale: Locale;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = field.label[locale] ?? field.label.sk;
  return (
    <div>
      <Label>
        {label}
        {field.required && <span className="text-error ml-0.5">*</span>}
      </Label>
      {field.type === 'text' && (
        <Input
          value={typeof value === 'string' ? value : ''}
          maxLength={field.max}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.type === 'textarea' && (
        <textarea
          value={typeof value === 'string' ? value : ''}
          maxLength={field.max}
          rows={4}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong px-4 py-3 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
        />
      )}
      {field.type === 'number' && (
        <Input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      )}
      {field.type === 'select' && (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full h-11 rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong px-4 font-sans text-body text-text-primary hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
        >
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}
      {field.type === 'multi_select' && (
        <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
          {(field.options ?? []).map((o) => {
            const arr = Array.isArray(value) ? (value as string[]) : [];
            const checked = arr.includes(o);
            return (
              <label
                key={o}
                className={cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 font-sans text-body-small cursor-pointer transition-all border',
                  checked
                    ? 'bg-primary/8 border-primary/25 text-primary-dark'
                    : 'bg-white/70 border-glass-border-strong text-text-primary hover:border-primary/20',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange(checked ? arr.filter((x) => x !== o) : [...arr, o])
                  }
                  className="h-4 w-4 accent-primary"
                />
                {o}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
